/**
 * Expo Ortho — <facets-form>: filtering, sorting and pagination without page reloads.
 * Wraps the toolbar, the filter drawer (#FacetsForm) and the results of a collection or search page.
 * Every control keeps working as a plain GET form / link when this script is missing.
 */
(function () {
  'use strict';

  if (customElements.get('facets-form')) return;

  const desktop = window.matchMedia('(min-width: 1100px)');
  const PRICE_INPUT = 'input[type="number"]';
  const PRICE_DELAY = 400;

  class FacetsForm extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.controller = null;
      this.timer = null;

      this.onInput = this.onInput.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onPopstate = this.onPopstate.bind(this);
      this.syncSidebar = this.syncSidebar.bind(this);

      this.addEventListener('input', this.onInput);
      this.addEventListener('change', this.onChange);
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('click', this.onClick);
      window.addEventListener('popstate', this.onPopstate);
      desktop.addEventListener('change', this.syncSidebar);

      // <theme-drawer> makes itself inert when it connects, which happens after this callback on re-renders.
      queueMicrotask(this.syncSidebar);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopstate);
      desktop.removeEventListener('change', this.syncSidebar);
      clearTimeout(this.timer);
      if (this.controller) this.controller.abort();
    }

    get form() {
      return this.querySelector('#FacetsForm');
    }

    get drawer() {
      return this.querySelector('.facets-drawer');
    }

    /* ---- Sidebar layout: on desktop the drawer shell is a static column, not a dialog ---- */

    syncSidebar() {
      const drawer = this.drawer;
      if (!drawer || !this.classList.contains('facets-layout--sidebar')) return;

      const inline = desktop.matches;
      const panel = drawer.querySelector('.drawer__panel');

      if (inline && drawer.hasAttribute('open') && typeof drawer.close === 'function') drawer.close();
      drawer.inert = inline ? false : !drawer.hasAttribute('open');

      if (panel) {
        panel.setAttribute('role', inline ? 'region' : 'dialog');
        if (inline) panel.removeAttribute('aria-modal');
        else panel.setAttribute('aria-modal', 'true');
      }
    }

    /* ---- Events ---- */

    onInput(event) {
      if (event.target.matches(PRICE_INPUT)) this.submitSoon();
    }

    onChange(event) {
      const target = event.target;

      if (target.hasAttribute('data-sort-mirror')) {
        const sort = this.querySelector('select[name="sort_by"]');
        if (sort) sort.value = target.value;
        this.submit();
        return;
      }

      if (target.matches(PRICE_INPUT)) {
        this.submitSoon();
        return;
      }

      if (target.form && target.form.id === 'FacetsForm') this.submit();
    }

    onSubmit(event) {
      if (event.target.id !== 'FacetsForm') return;
      event.preventDefault();
      this.submit();
    }

    onClick(event) {
      const more = event.target.closest('[data-facet-more]');
      if (more) {
        const group = more.closest('[data-facet-group]');
        const expanded = group.classList.toggle('is-expanded');
        more.setAttribute('aria-expanded', String(expanded));
        return;
      }

      const link = event.target.closest('[data-facet-link]');
      if (!link || !link.href || event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      event.preventDefault();
      this.load(link.href, { scroll: true });
    }

    onPopstate() {
      this.load(window.location.href, { push: false });
    }

    /* ---- Loading ---- */

    submitSoon() {
      clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.submit(), PRICE_DELAY);
    }

    /** Builds the URL from the form (empty fields dropped, back to page 1) and loads it. */
    submit() {
      clearTimeout(this.timer);
      const form = this.form;
      if (!form) return;

      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        if (String(value).trim() !== '') params.append(key, value);
      });

      const query = params.toString();
      this.load(form.action.split('?')[0] + (query ? `?${query}` : ''), { scroll: true });
    }

    async load(url, { push = true, scroll = false } = {}) {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;

      this.classList.add('is-loading');
      this.setAttribute('aria-busy', 'true');

      try {
        const separator = url.includes('?') ? '&' : '?';
        const response = await fetch(`${url}${separator}section_id=${this.sectionId}`, { signal });
        if (!response.ok) throw new Error(response.statusText);

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (!doc.querySelector('[data-facets-region="results"]')) throw new Error('Unexpected response');

        this.render(doc);
        if (push) window.history.pushState({ facets: true }, '', url);
        theme.utils.emit('facets:rendered', { sectionId: this.sectionId });
        if (scroll) this.scrollToResults();
      } catch (error) {
        if (error.name === 'AbortError') return;
        window.location.href = url;
        return;
      } finally {
        if (!signal.aborted) {
          this.classList.remove('is-loading');
          this.removeAttribute('aria-busy');
          this.controller = null;
        }
      }
    }

    /** Swaps every [data-facets-region], keeping open filter groups and the focused control. */
    render(doc) {
      const openGroups = new Set();
      const expandedGroups = new Set();
      this.querySelectorAll('[data-facet-group]').forEach((group) => {
        if (group.open) openGroups.add(group.id);
        if (group.classList.contains('is-expanded')) expandedGroups.add(group.id);
      });

      const active = document.activeElement;
      const wasInside = this.contains(active);
      const focusedId = wasInside ? active.id : '';
      const typing = focusedId && active.matches(PRICE_INPUT) ? active.value : null;

      this.querySelectorAll('[data-facets-region]').forEach((region) => {
        const next = doc.querySelector(`[data-facets-region="${region.dataset.facetsRegion}"]`);
        if (next) region.replaceWith(next);
      });

      this.querySelectorAll('[data-facet-group]').forEach((group) => {
        if (group.tagName === 'DETAILS') group.open = openGroups.has(group.id);
        const expanded = expandedGroups.has(group.id);
        group.classList.toggle('is-expanded', expanded);
        const more = group.querySelector('[data-facet-more]');
        if (more) more.setAttribute('aria-expanded', String(expanded));
      });

      const focused = focusedId ? this.querySelector(`#${CSS.escape(focusedId)}`) : null;
      if (focused) {
        if (typing !== null) focused.value = typing;
        focused.focus({ preventScroll: true });
      } else if (wasInside) {
        // The clicked control was replaced: keep keyboard focus inside an open drawer.
        const drawer = this.drawer;
        const panel = drawer && drawer.hasAttribute('open') ? drawer.querySelector('.drawer__panel') : null;
        if (panel) panel.focus({ preventScroll: true });
      }
    }

    scrollToResults() {
      const results = this.querySelector('[data-facets-region="results"]');
      if (!results) return;

      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 68;
      const top = results.getBoundingClientRect().top;
      if (top >= header) return;

      window.scrollTo({ top: window.scrollY + top - header - 16, behavior: 'smooth' });
    }
  }

  customElements.define('facets-form', FacetsForm);
})();
